import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Line } from "react-chartjs-2";
import { getTransactionsChartDataAction } from "../../Actions/TransactionActions";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const DashboardTransactionGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Transactions",
        data: [],
        borderColor: "#344D95",
        backgroundColor: "rgba(46, 125, 50, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  });
  const [loading, setLoading] = useState(false);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getTransactionsChartDataAction({
        startDate: startDate,
        endDate: endDate,
        merchant: selectedMerchant,
        chartType: selectedTimeRange,
        websiteId: selectedWebsite || null,
      });

      if (response?.data?.labels && response?.data?.data) {
        setChartData({
          labels: response.data.labels,
          datasets: [
            {
              label: "Transactions",
              data: response.data.data,
              borderColor: "#344D95",
              backgroundColor: "rgba(46, 125, 50, 0.1)",
              borderWidth: 2,
              tension: 0.4,
              fill: true,
            },
          ],
        });
      } else {
        setChartData({
          labels: [],
          datasets: [
            {
              label: "Transactions",
              data: [],
              borderColor: "#344D95",
              backgroundColor: "rgba(46, 125, 50, 0.1)",
              borderWidth: 2,
              tension: 0.4,
              fill: true,
            },
          ],
        });
      }
    } catch (error) {
      setChartData({
        labels: [],
        datasets: [
          {
            label: "Transactions",
            data: [],
            borderColor: "#344D95",
            backgroundColor: "rgba(46, 125, 50, 0.1)",
            borderWidth: 2,
            tension: 0.4,
            fill: true,
          },
        ],
      });
      if (error?.response?.data?.message) {
        onShowSnackbar(error.response.data.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, selectedTimeRange, onShowSnackbar]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, fetchChartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Monthly Transactions" },
    },
    scales: {
      x: { title: { display: true, text: "Date / Month" } },
      y: {
        title: { display: true, text: "Number of Transactions" },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`} key="totalTransactionCountChart">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("totalTransactionCountChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Total Transactions Count</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading transactions data...</p>
            </div>
          ) : chartData.datasets[0].data.length === 0 || chartData.datasets[0].data.every((value) => value === 0) ? (
            <NoDataFound message="No transaction data available" />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTransactionGraph;